import { apiDelete, apiGet, apiPatch, apiPost } from '../../lib/apiClient'
import type { IGitService, RepoReadinessReport, RepoInitOutcome } from './IGitService.interface'
import type {
  Branch,
  BranchFileChange,
  CIStatus,
  Commit,
  CommitDetail,
  CommitFilter,
  Comparison,
  CreatePRInput,
  CreateReleaseInput,
  FileDiff,
  PR,
  PRDetail,
  PRFilter,
  Release,
  Repo,
  RepoRef,
  RunFilter,
  TreeEntry,
  WorkflowRun,
  PrApprovalState,
} from '../../types/git.types'
import type {
  MergeConflictReport,
  LandReviewReport,
  ResolveMergeConflictsInput,
  ResolveMergeConflictsResult,
} from '@stride/shared'

// ── Branch type helper (single source of truth in ./branchType) ───────────────

export { deriveBranchType } from './branchType'

// ── AzureDevOpsGitService ─────────────────────────────────────────────────────

export class AzureDevOpsGitService implements IGitService {
  private base(ref: RepoRef): string {
    return `/api/git/${encodeURIComponent(ref.owner)}/${encodeURIComponent(ref.repo)}`
  }

  // ── Repos ──────────────────────────────────────────────────────────────────

  // `owner` is derived client-side from fullName so the backend wire format
  // doesn't need updating (avoids touching 31 backend tests).
  private withOwner(r: Repo): Repo {
    return { ...r, owner: r.owner || r.fullName.split('/')[0] || '' }
  }

  listRepos(): Promise<Repo[]> {
    return apiGet<Repo[]>('/api/git/repos').then((repos) => repos.map((r) => this.withOwner(r)))
  }

  getRepo(ref: RepoRef): Promise<Repo> {
    return apiGet<Repo>(`${this.base(ref)}`).then((r) => this.withOwner(r))
  }

  getReadiness(ref: RepoRef): Promise<RepoReadinessReport> {
    return apiGet<RepoReadinessReport>(`${this.base(ref)}/readiness`)
  }

  initializeRepo(ref: RepoRef, confirm: true): Promise<RepoInitOutcome> {
    // `confirm` travels to the server, which refuses without it — the guard lives at the boundary that writes,
    // not only in the component that asks.
    return apiPost<RepoInitOutcome>(`${this.base(ref)}/initialize`, { confirm })
  }

  // ── Branches ──────────────────────────────────────────────────────────────

  listBranches(ref: RepoRef): Promise<Branch[]> {
    return apiGet<Branch[]>(`${this.base(ref)}/branches`)
  }

  getBranch(ref: RepoRef, branch: string): Promise<Branch> {
    return apiGet<Branch>(`${this.base(ref)}/branches/${encodeURIComponent(branch)}`)
  }

  createBranch(ref: RepoRef, name: string, fromBranch: string): Promise<Branch> {
    return apiPost<Branch>(`${this.base(ref)}/branches`, { name, fromBranch })
  }

  async deleteBranch(ref: RepoRef, branch: string): Promise<void> {
    await apiDelete(`${this.base(ref)}/branches/${encodeURIComponent(branch)}`)
  }

  // ── Commits ────────────────────────────────────────────────────────────────

  listCommits(ref: RepoRef, filter?: CommitFilter): Promise<Commit[]> {
    const params = filter ? `?${new URLSearchParams(filter as Record<string, string>).toString()}` : ''
    return apiGet<Commit[]>(`${this.base(ref)}/commits${params}`)
  }

  getCommit(ref: RepoRef, sha: string): Promise<CommitDetail> {
    return apiGet<CommitDetail>(`${this.base(ref)}/commits/${encodeURIComponent(sha)}`)
  }

  // ── Diffs ──────────────────────────────────────────────────────────────────

  compareRefs(ref: RepoRef, base: string, head: string): Promise<Comparison> {
    return apiGet<Comparison>(
      `${this.base(ref)}/compare/${encodeURIComponent(base)}...${encodeURIComponent(head)}`,
    )
  }

  getFileDiff(ref: RepoRef, base: string, head: string, filePath: string): Promise<FileDiff> {
    return apiGet<FileDiff>(
      `${this.base(ref)}/diff?base=${encodeURIComponent(base)}&head=${encodeURIComponent(head)}&path=${encodeURIComponent(filePath)}`,
    )
  }

  // ── Branch file browsing + cross-branch compare ──────────────────────────────

  getBranchTree(ref: RepoRef, branch: string): Promise<TreeEntry[]> {
    return apiGet<TreeEntry[]>(`${this.base(ref)}/tree?branch=${encodeURIComponent(branch)}`)
  }

  readBranchFile(ref: RepoRef, branch: string, path: string): Promise<string> {
    return apiGet<{ content: string }>(
      `${this.base(ref)}/file?branch=${encodeURIComponent(branch)}&path=${encodeURIComponent(path)}`,
    ).then((r) => r.content)
  }

  compareBranchFiles(ref: RepoRef, base: string, head: string): Promise<BranchFileChange[]> {
    // Derived from the existing ref comparison (GitHub returns the changed files with status).
    return this.compareRefs(ref, base, head).then((c) =>
      c.files.map((f): BranchFileChange => ({
        path: f.filename,
        status: f.status === 'added' ? 'added' : f.status === 'deleted' ? 'removed' : 'modified',
      })),
    )
  }

  // ── Pull Requests ──────────────────────────────────────────────────────────

  listPRs(ref: RepoRef, filter?: PRFilter): Promise<PR[]> {
    const params = filter ? `?${new URLSearchParams(filter as Record<string, string>).toString()}` : ''
    return apiGet<PR[]>(`${this.base(ref)}/pulls${params}`)
  }

  getPR(ref: RepoRef, prNumber: number): Promise<PRDetail> {
    return apiGet<PRDetail>(`${this.base(ref)}/pulls/${prNumber}`)
  }

  createPR(ref: RepoRef, input: CreatePRInput): Promise<PR> {
    return apiPost<PR>(`${this.base(ref)}/pulls`, input)
  }

  updatePR(ref: RepoRef, prNumber: number, patch: { title?: string; body?: string }): Promise<PR> {
    return apiPatch<PR>(`${this.base(ref)}/pulls/${prNumber}`, patch)
  }

  async mergePR(ref: RepoRef, prNumber: number, method: 'merge' | 'squash' | 'rebase'): Promise<void> {
    await apiPost<void>(`${this.base(ref)}/pulls/${prNumber}/merge`, { method })
  }

  getConflicts(ref: RepoRef, prNumber: number): Promise<MergeConflictReport> {
    return apiGet<MergeConflictReport>(`${this.base(ref)}/pulls/${prNumber}/conflicts`)
  }

  resolveConflicts(ref: RepoRef, prNumber: number, input: ResolveMergeConflictsInput): Promise<ResolveMergeConflictsResult> {
    return apiPost<ResolveMergeConflictsResult>(`${this.base(ref)}/pulls/${prNumber}/resolve`, input)
  }

  prApproval(ref: RepoRef, prNumber: number): Promise<PrApprovalState> {
    return apiGet<PrApprovalState>(`${this.base(ref)}/pulls/${prNumber}/approval`)
  }

  landReview(ref: RepoRef, base: string, head: string, scopePaths?: string[]): Promise<LandReviewReport> {
    const q = new URLSearchParams({ base, head })
    if (scopePaths && scopePaths.length > 0) q.set('scope', scopePaths.join(','))
    return apiGet<LandReviewReport>(`${this.base(ref)}/land-review?${q.toString()}`)
  }

  // ── Releases ───────────────────────────────────────────────────────────────

  listReleases(ref: RepoRef): Promise<Release[]> {
    return apiGet<Release[]>(`${this.base(ref)}/releases`)
  }

  getLatestRelease(ref: RepoRef): Promise<Release | null> {
    return apiGet<Release | null>(`${this.base(ref)}/releases/latest`)
  }

  createRelease(ref: RepoRef, input: CreateReleaseInput): Promise<Release> {
    return apiPost<Release>(`${this.base(ref)}/releases`, input)
  }

  // ── CI / Workflows ─────────────────────────────────────────────────────────

  listWorkflowRuns(ref: RepoRef, filter?: RunFilter): Promise<WorkflowRun[]> {
    const params = filter ? `?${new URLSearchParams(filter as Record<string, string>).toString()}` : ''
    return apiGet<WorkflowRun[]>(`${this.base(ref)}/runs${params}`)
  }

  getCommitStatus(ref: RepoRef, sha: string): Promise<CIStatus> {
    return apiGet<CIStatus>(`${this.base(ref)}/commits/${encodeURIComponent(sha)}/status`)
  }

  // ── F3 — Repo scaffolding ──────────────────────────────────────────────────

  scaffoldRepo(ref: RepoRef): Promise<{ sha: string; message: string }> {
    return apiPost<{ sha: string; message: string }>(`${this.base(ref)}/scaffold`, {})
  }
}

export const azureDevOpsGitService = new AzureDevOpsGitService()
